/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import Stack from "@mui/material/Stack";
import {
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";

import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import Div from "@jumbo/shared/Div";
import { useDispatch, useSelector } from "react-redux";
import Loader from "@jumbo/components/Loader";
import { userType } from "app/helpers/userType";
import {
  sampleEditById,
  sampleReturnById,
  sampleReturnList,
  SAMPLES_ADD,
} from "app/routes/constant";
import { useNavigate } from "react-router-dom";
import { deleteSample, getSampleList } from "app/redux/slice/sampleSlice";
import { useSnackbar } from "notistack";
import moment from "moment";
import Span from "@jumbo/shared/Span";
import { Icon } from "@iconify/react";

const STORAGE_KEY = "samples_filters";

const SampleManagement = () => {
  const { stageAction } = useDisclosure();
  const dispatch = useDispatch();
  const { isMA } = userType();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const sampleData = useSelector((state) => state?.sample);
  const [status, setStatus] = useState("All");

  const [searchText, setSearchText] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "uniqueVariant" });

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission"))
  );

  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
    returnPermission: false,
  });

  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
        returnPermission: true,
      });
    } else {
      let permissionValue = {
        createPermission: false,
        updatePermission: false,
        deletePermission: false,
        returnPermission: false,
      };

      permissionData?.length &&
        permissionData.forEach(function (num) {
          if (num?.name === "create-sample") {
            permissionValue.createPermission = true;
          }
          if (num?.name === "delete-sample") {
            permissionValue.deletePermission = true;
          }
          if (num?.name === "update-sample") {
            permissionValue.updatePermission = true;
          }
          if (num?.name === "return-sample") {
            permissionValue.returnPermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  const columns = [
    {
      id: "takenDate",
      label: "Taken Date",
      sort: true,
      render: (elm) => <> {moment(elm?.takenDate).format("DD/MM/YYYY")}</>,
    },
    { id: "sampleNumber", label: "Sample No", sort: true },
    {
      id: "vendorId",
      label: "Vendor",
      render: (row) => <>{row?.vendorId?.name}</>,
    },
    {
      id: "status",
      label: "Status",
      align: "center",
      render: (row) =>
        row?.status === 3 ? (
          <Span style={{ color: "green" }}>Returned</Span>
        ) : row?.status === 2 ? (
          <Span style={{ color: "#d77700" }}>Partial return</Span>
        ) : (
          <Span style={{ color: "red" }}>Taken</Span>
        ),
    },
    {
      id: "actions",
      label: "Actions",
      align: "center",
      render: (row) => (
        <>
          <IconButton
            disabled={!permission.updatePermission}
            onClick={() => {
              stageAction(row);
              navigate(sampleEditById(row._id));
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            disabled={!permission.returnPermission || row?.status === 3}
            onClick={() => {
              stageAction(row);
              navigate(sampleReturnById(row._id));
            }}
          >
            <Icon
              icon="carbon:ibm-data-product-exchange"
              width="22"
              height="22"
            />
          </IconButton>
          <IconButton
            disabled={row?.status === 1}
            onClick={() => {
              navigate(sampleReturnList(row._id));
            }}
          >
            <FormatListBulletedIcon />
          </IconButton>
          <IconButton
            disabled={!permission.deletePermission}
            onClick={() => {
              deleteStageAction(row);
            }}
          >
            <DeleteIcon color={"error"} />
          </IconButton>
        </>
      ),
    },
  ];

  const DeleteSample = () => {
    closeDelete();
    dispatch(deleteSample(deleteActionProps?._id))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "success",
          });
          getData();
          closeDelete();
        } else {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "error",
          });
        }
      });
  };

  const getData = () => {
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
      ...(status !== "All" && {
        status,
      }),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getSampleList(value));
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      // save filters
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ searchText, status })
        );
      } catch (e) {}
      handleChangePage(0, 0);
      getData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  useEffect(() => {
    getPermission();
  }, []);

  useEffect(() => {
    // Restore filters from localStorage and auto-apply on first mount
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved) {
        const savedSearch = saved?.searchText ?? "";
        const savedStatus = saved?.status ?? "All";
        setSearchText(savedSearch);
        setStatus(savedStatus);
        // Build params explicitly to avoid async state timing
        const value = {
          page: page,
          perPage: rowsPerPage,
          search: String(savedSearch).trim(),
          ...(savedStatus !== "All" && { status: savedStatus }),
        };
        if (orderBy !== "") {
          value.sort = order === "asc" ? 1 : -1;
          value.field = orderBy;
        }
        handleChangePage(0, 0);
        dispatch(getSampleList(value));
        return;
      }
    } catch (e) {}
    getData();
  }, [page, rowsPerPage, dispatch, order, orderBy]);

  return (
    <>
      {sampleData?.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={{ xs: 1, md: 3 }}>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={() => {
                              setSearchText("");
                              try {
                                localStorage.setItem(
                                  STORAGE_KEY,
                                  JSON.stringify({ searchText: "", status })
                                );
                              } catch (e) {}
                            }}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        name="isInvoiceCreated"
                        id="isInvoiceCreated"
                        value={status}
                        label="Status"
                        size="small"
                        onChange={(e) => {
                          const nextStatus = e.target.value;
                          setStatus(nextStatus);
                          try {
                            localStorage.setItem(
                              STORAGE_KEY,
                              JSON.stringify({ searchText, status: nextStatus })
                            );
                          } catch (e) {}
                        }}
                      >
                        <MenuItem value={"All"}>All</MenuItem>
                        <MenuItem value={1}>Taken</MenuItem>
                        <MenuItem value={2}>Partial Return</MenuItem>
                        <MenuItem value={3}>Returned</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        try {
                          localStorage.setItem(
                            STORAGE_KEY,
                            JSON.stringify({ searchText, status })
                          );
                        } catch (e) {}
                        handleChangePage(0, 0);
                        getData();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Sample Product List
                </Typography>
                {permission.createPermission && (
                  <LoadingButton
                    onClick={() => {
                      navigate(SAMPLES_ADD);
                    }}
                    type="submit"
                    variant="contained"
                    sx={{
                      mt: { xs: 1, md: 0 },
                    }}
                  >
                    Add Sample
                  </LoadingButton>
                )}
              </Stack>
              <Table
                columns={columns}
                rows={sampleData?.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={sampleData?.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                editAction={stageAction}
              />
            </CardContent>
          </Card>
          <DialogConfirm
            isOpen={isDelete}
            onClose={closeDelete}
            title="Are you sure you want to delete the sample product?"
            onConfirm={DeleteSample}
            btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
          />
        </>
      )}
    </>
  );
};

export default SampleManagement;
